import { useNavigate, useSearchParams } from "react-router-dom"
import PageContainer from "../components/PageContainer"
import { useEffect, useState } from "react";
import PageTitle from "../components/PageTitle";
import { useDispatch, useSelector } from "react-redux";
import { getAllCompanies, setIsNeedCompaniesRefresh } from "../redux/adminCompaniesSlice";
import Loader from "../components/Loader";
import CustomHr from "../components/CustomHr";
import { GreenButton } from "../components/buttons";
import { setBacklink } from "../redux/backlinkSlice";

const AddEditCompany = () => {

    // Verifico se è una add o una edit
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId');
    const [isEditMode, setIsEditMode] = useState(false);
    const [companyData, setCompanyData] = useState(null);
    const { data, isLoading } = useSelector(state => state.adminCompanies);
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setBacklink("handle-companies")) // Imposto il backlinkn su HandleCompanies
    }, [dispatch])
    useEffect(() => {
        if (companyId && !data) {
            dispatch(getAllCompanies());
        } else if (companyId && data) {
            setIsEditMode(true);
            setCompanyData(data.find(element => element._id === companyId));
        }
    }, [companyId, data, dispatch]);

    // Imposto inputData con i dati dell'attività trovata
    useEffect(() => {
        if (companyData) {
            setInputData({
                _id: companyData._id,
                companyName: companyData.companyName,
                companyType: companyData.companyType,
                IDI: companyData.IDI,
                street: companyData.address.street,
                city: companyData.address.city,
                postalCode: companyData.address.postalCode,
                phone: companyData.phone,
                email: companyData.email,
                website: companyData.website,
                notes: companyData.notes,
                dataForPurchase: companyData.dataForPurchase ? companyData.dataForPurchase : [],
                countryCode: companyData.companyData
            })
        }
    }, [companyData])

    // Handle inputData
    const initialState = {
        companyName: null,
        companyType: null,
        IDI: null,
        street: null,
        city: null,
        postalCode: null,
        phone: null,
        email: null,
        website: null,
        notes: null,
        dataForPurchase: [],
        countryCode: 'CH'
    }
    const [inputData, setInputData] = useState(initialState);
    const handleInputData = (event) => {
        const { id, value } = event.target;
        setInputData(prevState => ({
            ...prevState,
            [id]: value
        }))
    }
    // Gestisco UX campi obbligatori
    const [isFieldsError, setIsFieldsError] = useState(false);
    const [fieldsChecked, setFieldsChecked] = useState(false);
    useEffect(() => {
        let requiredFields = {
            companyName: false,
            street: false,
            city: false,
            postalCode: false,
        };
        inputData.companyName ? requiredFields.companyName = true : requiredFields.companyName = false;
        inputData.street ? requiredFields.street = true : requiredFields.street = false;
        inputData.city ? requiredFields.city = true : requiredFields.city = false;
        inputData.postalCode ? requiredFields.postalCode = true : requiredFields.postalCode = false;
        inputData.email ? requiredFields.email = true : requiredFields.email = false;
        let allFieldsAreTrue = Object.values(requiredFields).every(Boolean);
        allFieldsAreTrue ? setFieldsChecked(true) : setFieldsChecked(false);
    }, [inputData]);
    const handleCheckFieldsAndSendData = () => {
        fieldsChecked ? sendData() : setIsFieldsError(true);
    };
    useEffect(() => {
        setTimeout(() => {
            setIsFieldsError(false);
        }, 3000);
    }, [isFieldsError])

    // Fetch per l'invio dei dati
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [fetchErrorDetail, setFetchErrorDetail] = useState(null);
    const navigate = useNavigate();
    const sendData = async () => {
        setFetchStatus('loading');
        setFetchErrorDetail(null);
        const token = localStorage.getItem('ticino-wild-token');
        let url;
        if (isEditMode) {
            url = `${process.env.REACT_APP_SERVER_BASE_URL}/companies/edit-company`
        } else {
            url = `${process.env.REACT_APP_SERVER_BASE_URL}/companies/add-new-company`
        }
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
        const options = {
            method: 'POST',
            headers,
            body: JSON.stringify(inputData)
        };
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setFetchStatus('succeeded');
                setTimeout(() => {
                    dispatch(setIsNeedCompaniesRefresh(true));
                    navigate(`/company-details?companyId=${result.payload._id}`);
                }, 2000)
            } else {
                const error = await response.json();
                // Il server mette il dettaglio tecnico in `error` (es. E11000, validazione Mongoose)
                // e in `message` solo un testo generico: senza il dettaglio l'errore è indiagnosticabile.
                throw new Error(error.error || error.message || 'Errore sconosciuto'); // Solleva l'errore e passa al blocco catch
            }
        } catch (error) {
            console.error('Errore nella richiesta:', error);
            setFetchErrorDetail(error.message);
            setFetchStatus('failed');
        }
    }

    // Gestisco l'inserimento di elementi in dataForPurchase
    const handleDataForPurchase = (id, label) => {
        let newArray = [];
        if (!label && inputData.dataForPurchase.some(element => element.id === id)) {
            newArray = inputData.dataForPurchase.filter(element => element.id !== id);
        } else {
            newArray = [...inputData.dataForPurchase];
            newArray.push({id, label});
        }
        setInputData(prevState => ({
            ...prevState,
            dataForPurchase: newArray
        }))
    }

    return (
        <PageContainer px="4" desktopItems="center" isRelative>
            <PageTitle title={!isEditMode ? "Aggiungi azienda" : "Modifica azienda"} backLink="/handle-companies" />

            {/*  Loader con overlay nel caso in modalità edit si refreshi */}
            {
                isLoading &&
                <div className="absolute inset-0 z-10 bg-[#f7f7f8] bg-opacity-90">
                    <div className="w-full flex justify-center mt-24">
                        <Loader />
                    </div>
                </div>
            }


            {/* Informazioni generali */}
            <h2 className="mt-8">Informazioni azienda</h2>
            <CustomHr />

            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="companyName">Ragione sociale o persona di riferimento<span className="text-xl">*</span></label>
                <div className="flex gap-2">
                    <input type="text" id="companyName" className={`${inputData.dataForPurchase.some(element => element.id === 'companyName') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.companyName ? inputData.companyName : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'companyName') && 'hidden'}`} onClick={() => handleDataForPurchase('companyName', 'Nome azienda')}>+</div>
                </div>
            </div>
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="IDI">IDI</label>
                <div className="flex gap-2">
                    <input type="text" id="IDI" placeholder="(opzionale)" className={`${inputData.dataForPurchase.some(element => element.id === 'IDI') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.IDI ? inputData.IDI : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'IDI') && 'hidden'}`} onClick={() => handleDataForPurchase('IDI', 'IDI')}>+</div>
                </div>
            </div>
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="companyType">Tipo di azienda</label>
                <div className="flex gap-2">
                    <input type="text" id="companyType" placeholder="Azienda, persona fisica, associazione, ecc..." className={`${inputData.dataForPurchase.some(element => element.id === 'companyType') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.companyType ? inputData.companyType : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'companyType') && 'hidden'}`} onClick={() => handleDataForPurchase('companyType', 'Tipo di azienda')}>+</div>
                </div>
            </div>

            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit self-left">
                <label htmlFor="countryCode">Nazione</label>
                <select name="countryCode" id="countryCode" value={inputData.countryCode} onChange={handleInputData}>
                    <option value="CH">CH</option>
                    <option value="IT">IT</option>
                </select>
            </div>

            {/* Indirizzo */}
            <h3 className="mt-8">Indirizzo</h3>
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="street">Via <span className="text-xl">*</span></label>
                <div className="flex gap-2">
                    <input type="text" id="street" placeholder="Via e numero civico" className={`${inputData.dataForPurchase.some(element => element.id === 'street') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.street ? inputData.street : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'street') && 'hidden'}`} onClick={() => handleDataForPurchase('street', 'Indirizzo')}>+</div>
                </div>
            </div>
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="city">Città e provincia <span className="text-xl">*</span></label>
                <div className="flex gap-2">
                    <input type="text" id="city" placeholder="Città e provincia" className={`${inputData.dataForPurchase.some(element => element.id === 'city') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.city ? inputData.city : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'city') && 'hidden'}`} onClick={() => handleDataForPurchase('city', 'Città')}>+</div>
                </div>
            </div>
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="postalCode">CAP <span className="text-xl">*</span></label>
                <div className="flex gap-2">
                    <input type="text" id="postalCode" placeholder="CAP" className={`${inputData.dataForPurchase.some(element => element.id === 'postalCode') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.postalCode ? inputData.postalCode : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'postalCode') && 'hidden'}`} onClick={() => handleDataForPurchase('postalCode', 'Codice postale')}>+</div>
                </div>
            </div>
            {/* Email */}
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="email">Email <span className="text-xl">*</span></label>
                <div className="flex gap-2">
                    <input type="text" id="email" placeholder="Email" className={`${inputData.dataForPurchase.some(element => element.id === 'email') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.email ? inputData.email : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'email') && 'hidden'}`} onClick={() => handleDataForPurchase('email', 'Email')}>+</div>
                </div>
            </div>

            <h3 className="mt-8">Altre informazioni</h3>
            {/* Phone */}
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="phone">Telefono</label>
                <div className="flex gap-2">
                    <input type="text" id="phone" placeholder="Telefono" className={`${inputData.dataForPurchase.some(element => element.id === 'phone') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.phone ? inputData.phone : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'phone') && 'hidden'}`} onClick={() => handleDataForPurchase('phone', 'Phone')}>+</div>
                </div>
            </div>
            {/* Website */}
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="website">Website</label>
                <div className="flex gap-2">
                    <input type="text" id="website" placeholder="Website" className={`${inputData.dataForPurchase.some(element => element.id === 'website') ? 'lg:w-[800px]' : 'lg:w-[736px]'} flex-grow`} onChange={handleInputData} value={inputData.website ? inputData.website : null} />
                    <div className={`w-16 h-16 border-2 border-neutral-300 rounded-lg bg-white text-2xl flex justify-center items-center cursor-pointer ${inputData.dataForPurchase.some(element => element.id === 'website') && 'hidden'}`} onClick={() => handleDataForPurchase('website', 'Website')}>+</div>
                </div>
            </div>

            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="notes">Note</label>
                <textarea id="notes" className="resize-none lg:w-[800px]" onChange={handleInputData} value={inputData.notes ? inputData.notes : null} />
            </div>

            {/* Data Pool */}
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="dataForPurchase">Dati condivisi coi clienti all'acquisto delle attività:</label>
                <div className="border-2 radius-lg w-full lg:w-[800px] flex flex-col gap-2 min-h-16 p-4">
                    {
                        inputData.dataForPurchase && inputData.dataForPurchase.length > 0
                        ? inputData.dataForPurchase.map((element, index) => {
                            return <div key={index} className="w-full border border-neutral-800 py-2 px-3 rounded-lg flex justify-between items-center gap-4 text-lg">{element.label}<div className="pt-2"><i className="fi fi-sr-circle-xmark text-xl cursor-pointer" onClick={() => handleDataForPurchase(element.id)}></i></div></div>
                        })
                        : <p className="text-neutral-400 text-sm">Clicca il + accanto ai campi sopra per aggiungere dati da condividere con i clienti dopo l'acquisto.</p>
                    }
                </div>
            </div>

            <div className="mt-8">
                {fetchStatus === 'idle' && isFieldsError && <h4 className="text-red-500 mb-4">Compila tutti i campi obbligatori</h4>}
                {fetchStatus === 'idle' && !isFieldsError && <div className="w-full"><GreenButton text={isEditMode ? 'Salva modifiche' : 'Salva attività'} isMobileFull click={handleCheckFieldsAndSendData} /></div>}
                {fetchStatus === 'loading' && <Loader />}
                {fetchStatus === 'succeeded' && !isEditMode && <h3>Nuova azienda inserita correttamente, attendi il refresh della pagina</h3>}
                {fetchStatus === 'succeeded' && isEditMode && <h3>Attività modificata correttamente, attendi il refresh della pagina</h3>}
                {fetchStatus === 'failed' &&
                    <div>
                        <h3>Qualcosa è andato storto, ricarica la pagina e riprova.</h3>
                        {fetchErrorDetail && <p className="text-red-500 text-sm mt-2 break-words">Dettaglio errore: {fetchErrorDetail}</p>}
                    </div>
                }
            </div>
        </PageContainer>
    )
}

export default AddEditCompany